/**
 * WebSocket Service for real-time updates
 * 
 * Manages WebSocket connections for real-time reservation updates in the TapeChart.
 * 
 * Configuration:
 * Set REACT_APP_WEBSOCKET_URL in your .env file to configure the WebSocket endpoint:
 * 
 * Development (multi-process):
 *   REACT_APP_WEBSOCKET_URL=ws://localhost:8001
 * 
 * Development (single process):
 *   REACT_APP_WEBSOCKET_URL=ws://localhost:8000
 * 
 * Production (Railway):
 *   REACT_APP_WEBSOCKET_URL=wss://websocket-backend.up.railway.app
 * 
 * Custom domain:
 *   REACT_APP_WEBSOCKET_URL=wss://websocket.yourdomain.com
 * 
 * If REACT_APP_WEBSOCKET_URL is not set, the service will auto-detect:
 * - Development: ws://localhost:8001 (assumes multi-process setup)
 * - Production: Auto-generates based on current domain
 */

class WebSocketService {
  constructor() {
    this.ws = null;
    this.isConnected = false;
    this.isAuthenticated = false; // Track authentication state
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000; // Start with 1 second
    this.maxReconnectDelay = 30000; // Max 30 seconds
    this.hotelId = null;
    this.token = null;
    this.eventListeners = new Map();
    this.pingInterval = null;
    this.connectionPromise = null;
    this.isReconnecting = false;
  }

  /**
   * Connect to WebSocket server
   * @param {string} hotelId - Hotel ID for room-specific updates
   * @param {string} token - JWT authentication token
   * @returns {Promise} Connection promise
   */
  connect(hotelId, token) {
    if (this.connectionPromise) {
      return this.connectionPromise;
    }

    this.hotelId = hotelId;
    this.token = token;

    this.connectionPromise = new Promise((resolve, reject) => {
      try {
        // Determine WebSocket URL based on environment variable or fallback logic
        let wsBaseUrl;
        
        if (process.env.REACT_APP_WEBSOCKET_URL) {
          // Use configured WebSocket URL from environment variable
          wsBaseUrl = process.env.REACT_APP_WEBSOCKET_URL;
        } else {
          // Fallback to auto-detection based on environment
          const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
          
          if (process.env.NODE_ENV === 'production') {
            // Production: Use backend host (Railway deployment)
            const host = window.location.host.replace('frontend', 'backend');
            wsBaseUrl = `${protocol}//${host}`;
          } else {
            // Development: Use separate WebSocket port (8001) for multi-process setup
            wsBaseUrl = `${protocol}//localhost:8001`;
          }
        }
        
        const wsUrl = `${wsBaseUrl}/ws/reservations/hotel/${hotelId}/`;
        
        
        this.ws = new WebSocket(wsUrl);
        
        this.ws.onopen = () => {
          this.isConnected = true;
          this.reconnectAttempts = 0;
          this.reconnectDelay = 1000;
          this.isReconnecting = false;
          
          // Authenticate immediately after connection
          this.authenticate();
          
          // Start ping interval to keep connection alive
          this.startPingInterval();
          
          resolve();
        };
        
        this.ws.onmessage = (event) => {
          this.handleMessage(event);
        };
        
        this.ws.onclose = (event) => {
          this.isConnected = false;
          this.isAuthenticated = false; // Clear authenticated flag
          this.stopPingInterval();
          this.connectionPromise = null;
          
          // Attempt to reconnect unless explicitly closed
          if (event.code !== 1000 && !this.isReconnecting) {
            this.scheduleReconnect();
          }
        };
        
        this.ws.onerror = (error) => {
          console.error('WebSocket error:', error);
          reject(error);
        };
        
      } catch (error) {
        console.error('Failed to create WebSocket connection:', error);
        reject(error);
      }
    });

    return this.connectionPromise;
  }

  /**
   * Authenticate with the WebSocket server
   */
  authenticate() {
    if (this.isConnected && this.token) {
      this.send({
        type: 'authenticate',
        token: this.token
      });
    }
  }

  /**
   * Subscribe to reservation updates
   */
  subscribeToReservations() {
    if (this.isConnected) {
      this.send({
        type: 'subscribe_reservations'
      });
    }
  }

  /**
   * Send a message to the WebSocket server
   * @param {Object} data - Data to send
   */
  send(data) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    } else {
      console.warn('WebSocket not connected, cannot send message:', data);
    }
  }

  /**
   * Handle incoming WebSocket messages
   * @param {MessageEvent} event - WebSocket message event
   */
  handleMessage(event) {
    try {
      const data = JSON.parse(event.data);

      // Route assist.* messages to the executor — lazy import to avoid cycles
      if (typeof data.type === 'string' && data.type.startsWith('assist.')) {
        import('../utils/assistExecutor').then(({ handleAssistMessage, setWsSendFn }) => {
          // Ensure the executor has the send function bound to this instance
          setWsSendFn((msg) => this.send(msg));
          handleAssistMessage(data);
        }).catch((err) => {
          console.error('[WebSocketService] Failed to load assistExecutor:', err);
        });
        return;
      }

      switch (data.type) {
        case 'connection_status':
          if (data.status === 'connected') {
          }
          break;

        case 'auth_success':
          this.isAuthenticated = true; // Set authenticated flag
          this.subscribeToReservations();
          this.emit('authenticated', data);
          break;

        case 'auth_error':
          console.error('WebSocket authentication failed:', data.message);
          this.isAuthenticated = false; // Clear authenticated flag
          this.emit('auth_error', data);
          break;

        case 'subscription_success':
          this.emit('subscribed', data);
          break;

        case 'reservation_created':
          this.emit('reservation_created', data.reservation);
          break;

        case 'reservation_updated':
          this.emit('reservation_updated', data.reservation);
          break;

        case 'reservation_cancelled':
          this.emit('reservation_cancelled', data.reservation_id);
          break;

        case 'inventory_updated':
          this.emit('inventory_updated', data);
          break;

        case 'guest_request_event':
          // Guest service requests + room-service orders for the
          // front-desk Requests queue (badge, toast, chime)
          this.emit('guest_request_event', data);
          break;

        case 'guest_message_event':
          // Unified guest inbox (email + OTA chat): summary badge/toast
          // in MessagesContext, and thread/list refetch in MessagesPage.
          this.emit('guest_message_event', data);
          break;

        case 'pong':
          // Handle ping response
          break;

        case 'error':
          console.error('WebSocket error message:', data.message);
          this.emit('error', data);
          break;

        default:
          console.warn('Unknown WebSocket message type:', data.type);
      }
    } catch (error) {
      console.error('Failed to parse WebSocket message:', error);
    }
  }

  /**
   * Add event listener
   * @param {string} event - Event name
   * @param {Function} callback - Event callback
   */
  on(event, callback) {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, new Set());
    }
    this.eventListeners.get(event).add(callback);
  }

  /**
   * Remove event listener
   * @param {string} event - Event name
   * @param {Function} callback - Event callback
   */
  off(event, callback) {
    if (this.eventListeners.has(event)) {
      this.eventListeners.get(event).delete(callback);
    }
  }

  /**
   * Emit event to all listeners
   * @param {string} event - Event name
   * @param {*} data - Event data
   */
  emit(event, data) {
    if (this.eventListeners.has(event)) {
      this.eventListeners.get(event).forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in WebSocket event listener for ${event}:`, error);
        }
      });
    } else {
    }
  }

  /**
   * Start ping interval to keep connection alive
   */
  startPingInterval() {
    this.stopPingInterval(); // Clear any existing interval
    
    this.pingInterval = setInterval(() => {
      if (this.isConnected) {
        this.send({ type: 'ping' });
      }
    }, 30000); // Ping every 30 seconds
  }

  /**
   * Stop ping interval
   */
  stopPingInterval() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  /**
   * Schedule reconnection attempt
   */
  scheduleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached');
      this.emit('max_reconnect_attempts', { attempts: this.reconnectAttempts });
      return;
    }

    if (this.isReconnecting) {
      return;
    }

    this.isReconnecting = true;
    this.reconnectAttempts++;
    
    
    setTimeout(() => {
      if (this.hotelId && this.token) {
        this.connect(this.hotelId, this.token)
          .then(() => {
            this.emit('reconnected');
          })
          .catch((error) => {
            console.error('Reconnection failed:', error);
            this.isReconnecting = false;
            // Exponential backoff
            this.reconnectDelay = Math.min(this.reconnectDelay * 2, this.maxReconnectDelay);
            this.scheduleReconnect();
          });
      }
    }, this.reconnectDelay);
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect() {
    this.isReconnecting = false;
    this.stopPingInterval();
    
    if (this.ws) {
      this.ws.close(1000, 'Intentional disconnect');
      this.ws = null;
    }
    
    this.isConnected = false;
    this.isAuthenticated = false; // Clear authenticated flag
    this.connectionPromise = null;
    this.eventListeners.clear();
  }

  /**
   * Get connection status
   * @returns {boolean} Connection status
   */
  getConnectionStatus() {
    return this.isConnected;
  }

  /**
   * Get authentication status
   * @returns {boolean} Authentication status
   */
  getAuthenticationStatus() {
    return this.isAuthenticated;
  }

  /**
   * Update authentication token
   * @param {string} newToken - New JWT token
   */
  updateToken(newToken) {
    this.token = newToken;
    if (this.isConnected) {
      this.authenticate();
    }
  }

  /**
   * Get current WebSocket configuration for debugging
   * @returns {Object} Current configuration details
   */
  getConfiguration() {
    return {
      environment: process.env.NODE_ENV,
      configuredUrl: process.env.REACT_APP_WEBSOCKET_URL || 'Not configured',
      isConnected: this.isConnected,
      isAuthenticated: this.isAuthenticated,
      hotelId: this.hotelId,
      reconnectAttempts: this.reconnectAttempts,
      currentUrl: this.ws ? this.ws.url : 'No active connection'
    };
  }
}

// Create singleton instance
const webSocketService = new WebSocketService();

export default webSocketService;